import { useState } from 'react';
import { uploadImage } from '@/lib/admin';
import { FRENCH_PUBLIC_ENABLED } from '@/lib/i18n';

export function Field({ label, children, hint }) {
    return (
        <label className="block">
            <span className="label block mb-3" style={{ color: 'var(--ink-2)' }}>{label}</span>
            {children}
            {hint && <span className="caption block mt-2" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{hint}</span>}
        </label>
    );
}

/**
 * BilingualField — discreet EN/FR segmented control for translatable fields.
 *
 * The gallery owner edits ONE field at a time, toggling the locale via
 * two small text tabs (matches nav-link typography — no visual weight).
 * The English value stays under its original field name (`baseKey`).
 * The French value lives under `${baseKey}_fr`.
 *
 * A "FR missing" quietly-styled pill appears when the French value is
 * empty, so the owner can spot incomplete translations at a glance
 * without any redesign of the admin UI.
 *
 * Usage:
 *   <BilingualField label="Statement" hint="Short opening line" form={form} setForm={setForm} baseKey="statement">
 *     {(value, onChange) => <TextArea value={value} onChange={(e) => onChange(e.target.value)} rows={3} />}
 *   </BilingualField>
 */
export function BilingualField({ label, hint, form, setForm, baseKey, children }) {
    // NB: `useState` must be called unconditionally (rules of hooks). The
    // English-only branch below simply ignores the locale state.
    const [locale, setLocale] = useState('en');

    // English-only launch mode: hide the EN/FR tabs and the "FR missing" pill,
    // and only ever write to the English key. Any pre-existing `${baseKey}_fr`
    // value that was loaded into `form` remains in `form` state and is echoed
    // back to the API on save, so existing translations in MongoDB stay
    // untouched. When French is re-enabled by flipping FRENCH_PUBLIC_ENABLED
    // to `true`, the bilingual UI below returns automatically.
    if (!FRENCH_PUBLIC_ENABLED) {
        const enVal = form[baseKey] ?? '';
        const setEn = (newVal) => setForm({ ...form, [baseKey]: newVal });
        return (
            <Field label={label} hint={hint}>
                {children(enVal, setEn, 'en')}
            </Field>
        );
    }

    const enVal = form[baseKey] ?? '';
    const frVal = form[`${baseKey}_fr`] ?? '';
    const missingFr = !String(frVal || '').trim();
    const setVal = (newVal) => {
        const target = locale === 'fr' ? `${baseKey}_fr` : baseKey;
        setForm({ ...form, [target]: newVal });
    };
    const currentValue = locale === 'fr' ? frVal : enVal;
    return (
        <label className="block">
            <div className="flex items-center gap-4 mb-3">
                <span className="label" style={{ color: 'var(--ink-2)' }}>{label}</span>
                <span
                    data-testid={`lang-tabs-${baseKey}`}
                    role="tablist"
                    aria-label={`Locale for ${label}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={locale === 'en'}
                        onClick={() => setLocale('en')}
                        data-testid={`lang-tab-${baseKey}-en`}
                        className="label"
                        style={{
                            background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
                            color: 'var(--ink-2)',
                            opacity: locale === 'en' ? 1 : 0.5,
                            fontVariant: 'small-caps',
                        }}
                    >EN</button>
                    <span aria-hidden="true" style={{ color: 'var(--ink-3)', opacity: 0.4 }}>/</span>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={locale === 'fr'}
                        onClick={() => setLocale('fr')}
                        data-testid={`lang-tab-${baseKey}-fr`}
                        className="label"
                        style={{
                            background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
                            color: 'var(--ink-2)',
                            opacity: locale === 'fr' ? 1 : 0.5,
                            fontVariant: 'small-caps',
                        }}
                    >FR</button>
                </span>
                {missingFr && (
                    <span
                        className="caption"
                        title="No French translation yet — this record will remain hidden on French pages until the required French fields are provided."
                        style={{
                            marginLeft: 'auto', fontSize: 11, letterSpacing: '0.06em',
                            color: 'var(--ink-3)', opacity: 0.6,
                            textTransform: 'uppercase',
                        }}
                    >FR missing</span>
                )}
            </div>
            {children(currentValue, setVal, locale)}
            {hint && <span className="caption block mt-2" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{hint}</span>}
        </label>
    );
}

export function TextInput(props) {
    return <input {...props} className="input-line" />;
}

export function TextArea(props) {
    return <textarea {...props} className="input-line" rows={props.rows || 4} />;
}

export function Checkbox({ label, checked, onChange, testid }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid={testid} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
            <span className="caption" style={{ fontSize: 15 }}>{label}</span>
        </label>
    );
}

export function ImageUploader({ value, onChange, testid }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const pick = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setBusy(true); setErr('');
        try {
            const res = await uploadImage(f);
            onChange(res.url);
        } catch (e2) {
            setErr(e2.response?.data?.detail || 'Upload failed');
        } finally { setBusy(false); }
    };
    return (
        <div className="flex flex-col gap-3">
            {value ? (
                <div className="img-wrap aspect-[3/2] max-w-md">
                    <img src={value} alt="" />
                </div>
            ) : (
                <div className="aspect-[3/2] max-w-md flex items-center justify-center caption" style={{ background: 'var(--paper-2)', color: 'var(--ink-3)' }}>No image yet</div>
            )}
            <div className="flex gap-4 items-center">
                <label className="btn-ghost cursor-pointer">
                    {busy ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
                    <input data-testid={testid} type="file" accept="image/*" onChange={pick} className="hidden" disabled={busy} />
                </label>
                {value && <button type="button" onClick={() => onChange('')} className="label" style={{ color: 'var(--clay-deep)' }}>Remove</button>}
            </div>
            <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="or paste an image URL" className="input-line" style={{ fontSize: 14 }} />
            {err && <span className="caption" style={{ color: 'var(--clay-deep)' }}>{err}</span>}
        </div>
    );
}

export function ListInput({ value = [], onChange, placeholder }) {
    return (
        <div className="flex flex-col gap-3">
            {(value || []).map((v, i) => (
                <div key={i} className="flex gap-3 items-center">
                    <input value={v} onChange={(e) => onChange(value.map((x, j) => j === i ? e.target.value : x))} className="input-line flex-1" />
                    <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="label" style={{ color: 'var(--clay-deep)' }}>Remove</button>
                </div>
            ))}
            <button type="button" onClick={() => onChange([...(value || []), ''])} className="btn-ghost self-start">+ Add {placeholder || 'item'}</button>
        </div>
    );
}

/* MultiImageInput — an ordered list of image URLs, each with its own uploader,
   remove and reorder controls. Used for the exhibition Installation Gallery
   (unlimited images, mixed portrait/landscape). Keeps the same visual
   language as ImageUploader so the CMS feels consistent. */
export function MultiImageInput({ value = [], onChange, testidPrefix = 'multi-image' }) {
    const list = value || [];
    const update = (i, url) => onChange(list.map((v, j) => (j === i ? url : v)));
    const remove = (i) => onChange(list.filter((_, j) => j !== i));
    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= list.length) return;
        const copy = [...list];
        [copy[i], copy[j]] = [copy[j], copy[i]];
        onChange(copy);
    };
    const add = () => onChange([...list, '']);

    return (
        <div className="flex flex-col gap-8">
            {list.length === 0 && (
                <div className="caption" style={{ color: 'var(--ink-3)' }}>No gallery images yet — add images below to control exactly what appears in the exhibition Gallery.</div>
            )}
            {list.map((url, i) => (
                <div key={i} className="flex gap-6 items-start" data-testid={`${testidPrefix}-row-${i}`}>
                    <div style={{ flex: 1 }}>
                        <div className="flex items-baseline justify-between mb-3">
                            <span className="overline" style={{ color: 'var(--ink-3)' }}>Image {String(i + 1).padStart(2, '0')}</span>
                            <div className="flex gap-4 items-center">
                                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="label" style={{ color: i === 0 ? 'var(--ink-3)' : 'var(--ink-2)', cursor: i === 0 ? 'default' : 'pointer' }} data-testid={`${testidPrefix}-up-${i}`}>Up</button>
                                <button type="button" onClick={() => move(i, +1)} disabled={i === list.length - 1} className="label" style={{ color: i === list.length - 1 ? 'var(--ink-3)' : 'var(--ink-2)', cursor: i === list.length - 1 ? 'default' : 'pointer' }} data-testid={`${testidPrefix}-down-${i}`}>Down</button>
                                <button type="button" onClick={() => remove(i)} className="label" style={{ color: 'var(--clay-deep)' }} data-testid={`${testidPrefix}-remove-${i}`}>Remove</button>
                            </div>
                        </div>
                        <ImageUploader value={url} onChange={(u) => update(i, u)} testid={`${testidPrefix}-upload-${i}`} />
                    </div>
                </div>
            ))}
            <button type="button" onClick={add} className="btn-ghost self-start" data-testid={`${testidPrefix}-add`}>+ Add image</button>
        </div>
    );
}
